import { Chevron } from "@components/icons"
import { ButtonContainer } from "@components/MenuItem"
import MenuItemTogglableCompact, {
  MenuItemTogglableCompactProps,
} from "@components/MenuItemTogglableCompact"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import { body16BoldStyles, smallcapsBold13Styles } from "@constants/typography"
import useResizeHandler from "@hooks/useResizeHandler"
import React from "react"
import styled from "styled-components"

const Container = styled.div<{ interactive: boolean }>`
  pointer-events: ${({ interactive }) => (interactive ? "auto" : "none")};
`

const Label = styled(ButtonContainer)<{ expanded: boolean }>`
  position: relative;
  padding: 16px 10px 16px 24px;
  color: ${colors.mediumDarkGray};
  border: none;

  ${({ expanded }) =>
    expanded &&
    `
      ${body16BoldStyles}
      color: ${colors.black};
  `}
`

const CountWrapper = styled.div`
  position: absolute;
  right: 52px;
  width: 20px;
  height: 20px;
  background-color: ${colors.offBlack};
  border-radius: 50%;
`

const Count = styled.div`
  ${smallcapsBold13Styles}
  padding-top: 2.5px;
  color: ${colors.offWhite};
  font-size: 12px;
  letter-spacing: 0.9px;
`

const IconWrapper = styled.div<{ rotate?: number }>`
  width: 24px;
  height: 24px;
  margin-right: 5px;
  flex-shrink: 0;
  transition: transform ${animation.quick} ${cubicBezier};
  ${({ rotate }) => rotate && `transform: rotate(${rotate}deg);`}
`

const Content = styled.div<{
  expanded: boolean
  expandedHeight: number
}>`
  display: flex;
  position: relative;
  height: ${({ expanded, expandedHeight }) =>
    expanded ? `${expandedHeight}px` : 0};
  flex-direction: column;
  transition: height ${animation.quick} ${cubicBezier};
  overflow: hidden;

  ${ButtonContainer} {
    padding-left: 32px;
  }
`

export const anchorStylesMenu = `
  display: flex;
  position: absolute;
  width: 100%;
  padding-bottom: 10px;
  flex-wrap: wrap;
  visibility: hidden;
`

export const anchorStylesMenuItem = `
  width: 100%;
  height: 36px;
`

export const anchorStylesSideNav = `
  display: flex;
  position: absolute;
  width: calc(1.5 / 12 * 100vw);
  min-width: calc(300px - (0.5 / 12 * 100vw));
  padding: 18px 14px;
  flex-wrap: wrap;
  visibility: hidden;
`

export const sideNavRulerItemStyles = `
  width: 100%;
  padding: 8px 24px;
  line-height: 20px;
`

export const sideNavRulerAnchorStyles = `
  display: flex;
  position: absolute;
  width: calc(1.5 / 12 * 100vw);
  min-width: calc(300px - (0.5 / 12 * 100vw));
  padding: 0 14px;
  flex-wrap: wrap;
  visibility: hidden;
`

interface MenuItemAccordionProps {
  activeItemCount?: number
  anchorContainerStyles?: string
  anchorId?: string
  anchorItemStyles?: string
  expandByDefault?: boolean
  items: Array<MenuItemTogglableCompactProps>
  label: string
  useSideNavStyles?: boolean
}

const MenuItemAccordion: React.FC<MenuItemAccordionProps> = ({
  activeItemCount,
  expandByDefault,
  items,
  label,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = React.useState<boolean>(!!expandByDefault)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const calculateContentHeight = () => {
    const children = containerRef.current?.children
    let height = 0

    if (children) {
      for (let i = 0; i < children.length; i++) {
        const child = children[i]

        if (child instanceof HTMLElement) {
          height += child.getBoundingClientRect().height
        }
      }
    }

    return height
  }

  const [expandedHeight, setExpandedHeight] = React.useState<number>(0)

  React.useEffect(() => {
    setExpandedHeight(calculateContentHeight)
  }, [items])

  const handleResize = () => {
    const height = calculateContentHeight()

    if (height !== expandedHeight) {
      setExpandedHeight(height)
    }
  }

  useResizeHandler({
    onResize: handleResize,
    runForProps: [items],
  })

  return (
    <Container interactive={items.length > 0}>
      <Label expanded={items.length > 0 && expanded} onClick={toggleExpanded}>
        {label}
        {!!(activeItemCount && activeItemCount > 0) && (
          <CountWrapper>
            <Count>{activeItemCount}</Count>
          </CountWrapper>
        )}
        {items.length > 0 && (
          <IconWrapper rotate={expanded ? -90 : 90}>
            <Chevron />
          </IconWrapper>
        )}
      </Label>
      <Content
        expanded={expanded}
        expandedHeight={expandedHeight}
        ref={containerRef}
      >
        {items.map((item, i) => (
          <MenuItemTogglableCompact key={`${i}-${item.label}`} {...item} />
        ))}
      </Content>
    </Container>
  )
}

export default MenuItemAccordion
